import {
  Button,
  Group,
  Modal,
  Stack,
  Switch,
  Table,
  TagsInput,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type Synonym, useAdminMutation, useAdminQuery } from "../../api/admin";
import { api } from "../../api/client";

export function SynonymsPage() {
  const { t } = useTranslation();
  const list = useAdminQuery<{ items: Synonym[] }>("synonyms", "/api/admin/synonyms");
  const [editing, setEditing] = useState<Synonym | "new" | null>(null);
  const [term, setTerm] = useState("");
  const [variants, setVariants] = useState<string[]>([]);
  const [enabled, setEnabled] = useState(true);
  const save = useAdminMutation(
    (v: { id: number | null; term: string; variants: string[]; enabled: boolean }) =>
      v.id === null
        ? api("/api/admin/synonyms", {
            method: "POST",
            body: { lang: "hy", term: v.term, variants: v.variants, enabled: v.enabled },
          })
        : api(`/api/admin/synonyms/${v.id}`, {
            method: "PATCH",
            body: { term: v.term, variants: v.variants, enabled: v.enabled },
          }),
    ["synonyms"],
  );
  const remove = useAdminMutation(
    (id: number) => api(`/api/admin/synonyms/${id}`, { method: "DELETE" }),
    ["synonyms"],
  );
  const open = (s: Synonym | "new") => {
    setEditing(s);
    setTerm(s === "new" ? "" : s.term);
    setVariants(s === "new" ? [] : s.variants);
    setEnabled(s === "new" ? true : s.enabled);
  };

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {t("admin.synonyms.hint")}
        </Text>
        <Button onClick={() => open("new")}>{t("admin.synonyms.add")}</Button>
      </Group>
      <Table.ScrollContainer minWidth={640}>
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("admin.synonyms.term")}</Table.Th>
              <Table.Th>{t("admin.synonyms.variants")}</Table.Th>
              <Table.Th>{t("admin.ref.active")}</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(list.data?.items ?? []).map((s) => (
              <Table.Tr key={s.id} style={{ opacity: s.enabled ? 1 : 0.5 }}>
                <Table.Td>{s.term}</Table.Td>
                <Table.Td>{s.variants.join(", ")}</Table.Td>
                <Table.Td>{s.enabled ? "✓" : "—"}</Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <Button size="compact-xs" variant="subtle" onClick={() => open(s)}>
                      {t("common.edit")}
                    </Button>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="red"
                      onClick={() =>
                        remove
                          .mutateAsync(s.id)
                          .catch((e: Error) =>
                            notifications.show({ message: e.message, color: "red" }),
                          )
                      }
                    >
                      {t("common.delete")}
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            {list.data?.items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4} c="dimmed">
                  {t("admin.synonyms.empty")}
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Modal
        opened={editing !== null}
        onClose={() => setEditing(null)}
        title={t("admin.synonyms.edit")}
      >
        <Stack>
          <TextInput
            label={t("admin.synonyms.term")}
            value={term}
            onChange={(e) => setTerm(e.currentTarget.value)}
            required
          />
          <TagsInput
            label={t("admin.synonyms.variants")}
            description={t("admin.synonyms.variantsHint")}
            value={variants}
            onChange={setVariants}
            splitChars={[",", ";"]}
          />
          <Switch
            label={t("admin.ref.active")}
            checked={enabled}
            onChange={(e) => setEnabled(e.currentTarget.checked)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditing(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              loading={save.isPending}
              disabled={!term.trim() || variants.length === 0}
              onClick={async () => {
                try {
                  await save.mutateAsync({
                    id: editing === "new" ? null : (editing as Synonym).id,
                    term: term.trim(),
                    variants,
                    enabled,
                  });
                  notifications.show({ message: t("common.saved"), color: "green" });
                  setEditing(null);
                } catch (e) {
                  notifications.show({ message: (e as Error).message, color: "red" });
                }
              }}
            >
              {t("common.save")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
